import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Utilities
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Mock data
const mockUser = {
  id: 'user_001',
  email: 'test@yodlee.com',
  firstName: 'Test',
  lastName: 'User',
  role: 'user',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const TOKENS = {
  accessToken: 'mock-access-token',
  refreshToken: 'mock-refresh-token',
  expiresIn: 3600,
};

function ok(config: InternalAxiosRequestConfig, data: any, status = 200): AxiosResponse {
  return { data, status, statusText: 'OK', headers: {}, config };
}

function fail(config: InternalAxiosRequestConfig, status: number, message: string): AxiosResponse {
  return { data: { message }, status, statusText: 'Error', headers: {}, config };
}

export async function handleMockRequest(
  config: InternalAxiosRequestConfig,
): Promise<AxiosResponse | null> {
  const url = (config.url || '').replace(config.baseURL || '', '');
  const method = (config.method || 'get').toLowerCase();

  // Simulate network latency
  await sleep(350);

  // AUTH
  if (method === 'post' && url === '/auth/login') {
    const { email, password } = (config.data && JSON.parse(config.data as any)) || {};
    if (email === 'locked@yodlee.com') return fail(config, 403, 'Account locked');
    if (email === 'test@yodlee.com' && password === 'Passw0rd!m') {
      return ok(config, { user: mockUser, tokens: TOKENS });
    }
    return fail(config, 401, 'Invalid credentials');
  }

  if (method === 'post' && url === '/auth/logout') {
    return ok(config, { success: true });
  }

  if (method === 'get' && url === '/auth/me') {
    const auth = config.headers?.Authorization || config.headers?.authorization;
    if (auth === `Bearer ${TOKENS.accessToken}`) return ok(config, { user: mockUser });
    return fail(config, 401, 'Unauthorized');
  }

  if (method === 'post' && url === '/auth/refresh') {
    const body = (config.data && JSON.parse(config.data as any)) || {};
    if (body.refreshToken === TOKENS.refreshToken) {
      return ok(config, { accessToken: 'mock-access-token-2', expiresIn: 3600 });
    }
    return fail(config, 401, 'Invalid refresh token');
  }

  if (method === 'post' && url === '/auth/forgot-password') {
    const { email } = (config.data && JSON.parse(config.data as any)) || {};
    if (email === 'test@yodlee.com') return ok(config, { message: 'Reset email sent' });
    return fail(config, 404, 'Email not found');
  }

  if (method === 'post' && url === '/auth/reset-password') {
    const { token } = (config.data && JSON.parse(config.data as any)) || {};
    if (token === 'valid-reset-token') return ok(config, { message: 'Password reset successful' });
    return fail(config, 400, 'Invalid or expired token');
  }

  // OTP
  if (method === 'post' && (url === '/otp/generate' || url === 'otp/generate')) {
    return ok(config, { requestId: 'otp-req-123', destination: { type: 'email', value: 'test@yodlee.com' } });
  }
  if (method === 'post' && url === '/otp/verify') {
    const { code } = (config.data && JSON.parse(config.data as any)) || {};
    if (code === '123456') return ok(config, { verified: true });
    return fail(config, 400, 'Invalid code');
  }

  // UHID Connections (v1.1)
  if (method === 'get' && url === '/uhid/connections') {
    const apiVersion = (config.headers as any)?.['Api-Version'] || (config.headers as any)?.['api-version'];
    const authHeader = (config.headers as any)?.Authorization || (config.headers as any)?.authorization;
    if (apiVersion !== '1.1') return fail(config, 400, 'Unsupported API version');
    if (!authHeader) return fail(config, 401, 'Unauthorized');

    const response = {
      connections: [
        {
          cobrandId: 1001,
          cobrandName: 'Empower',
          memId: 2002,
          uhid: 33334444,
          providers: [
            {
              providerId: 501,
              providerName: 'Cherry Street Financial',
              logoUrl: '',
              favicon: '',
              authType: 'OAUTH',
              openBanking: true,
              consentScope: [
                {
                  scopeId: 'ACCOUNTS',
                  title: 'Accounts',
                  titleBody: ['Balances and identifiers as granted in consent'],
                },
                {
                  scopeId: 'TRANSACTIONS',
                  title: 'Transactions',
                  titleBody: ['Posted transactions including amount, merchant, date'],
                },
              ],
              accounts: [
                {
                  accountId: 'acc-emp-cherry',
                  name: 'Cherry Street Financial',
                  container: 'bank',
                  accountType: 'checking',
                  accountNumber: '•••• 1123',
                  balance: { amount: 2450.32, currency: 'USD' },
                },
                {
                  accountId: 'acc-emp-us',
                  name: 'US Bank',
                  container: 'bank',
                  accountType: 'savings',
                  accountNumber: '•••• 9988',
                  balance: { amount: 10234.11, currency: 'USD' },
                },
              ],
            },
            {
              providerId: 502,
              providerName: 'PayPal',
              logoUrl: '',
              favicon: '',
              accounts: [
                {
                  accountId: 'acc-pp-generic',
                  name: 'Generic Bank',
                  container: 'bank',
                  accountType: 'checking',
                  accountNumber: '•••• 2211',
                  balance: { amount: 560.55, currency: 'USD' },
                },
                {
                  accountId: 'acc-pp-us',
                  name: 'US Bank',
                  container: 'bank',
                  accountType: 'checking',
                  accountNumber: '•••• 8822',
                  balance: { amount: 1200.0, currency: 'USD' },
                },
              ],
            },
            {
              providerId: 503,
              providerName: 'Wealthfront',
              logoUrl: '',
              favicon: '',
              accounts: [
                {
                  accountId: 'acc-wf-generic',
                  name: 'Generic Bank',
                  container: 'bank',
                  accountType: 'checking',
                  accountNumber: '•••• 3344',
                  balance: { amount: 0, currency: 'USD' },
                },
                {
                  accountId: 'acc-wf-cherry',
                  name: 'Cherry Street Financial',
                  container: 'bank',
                  accountType: 'savings',
                  accountNumber: '•••• 5566',
                  balance: { amount: 0, currency: 'USD' },
                },
              ],
            },
          ],
        },
      ],
    };
    return ok(config, response);
  }

  // UHID Institutions (separate stub from connections)
  if (method === 'get' && url === '/uhid/institutions') {
    const apiVersion = (config.headers as any)?.['Api-Version'] || (config.headers as any)?.['api-version'];
    const authHeader = (config.headers as any)?.Authorization || (config.headers as any)?.authorization;
    if (apiVersion !== '1.1') return fail(config, 400, 'Unsupported API version');
    if (!authHeader) return fail(config, 401, 'Unauthorized');

    const response = {
      institutions: [
        {
          providerName: 'US Bank',
          logoUrl: 'https://d1id6p0py4hgs9.cloudfront.net/fastlink/v4/sites/fav-icons/13302.svg',
          cobrands: [
            { name: 'Empower', logoUrl: 'https://d1id6p0py4hgs9.cloudfront.net/FAVICON/FAV_31894.SVG' },
            { name: 'PayPal', logoUrl: 'https://d1id6p0py4hgs9.cloudfront.net/fastlink/v4/sites/fav-icons/3531.svg' },
          ],
        },
        {
          providerName: 'Cherry Street Financial',
          logoUrl: 'https://d1id6p0py4hgs9.cloudfront.net/fastlink/v4/sites/fav-icons/3049.svg',
          cobrands: [{ name: 'Empower', logoUrl: 'https://d1id6p0py4hgs9.cloudfront.net/FAVICON/FAV_31894.SVG' }],
        },
      ],
    };
    return ok(config, response);
  }

  // Unknown - let it pass through to real backend
  return null;
}

